// Modal "Log do Ticket" da tela de Relatórios (mockup Aeros): cabeçalho com
// contato/#id/fila/atendente/conexão/período e status, 4 metas (Espera,
// Duração, Mensagens, Motivo), abas Linha do tempo (GET /tickets-log/:id)
// e Conversa (GET /messages/:id), rodapé com NPS · Venda, PDF e atalho
// para Atendimentos.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import PictureAsPdfOutlinedIcon from "@material-ui/icons/PictureAsPdfOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import StarIcon from "@material-ui/icons/Star";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import html2pdf from "html2pdf.js";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { initialsOf, avatarColorFor } from "../Contacts/contactRules";
import { buildTimeline, describeLog, toneOf, timeLabel } from "../../components/ShowTicketLogModal/ticketLogRules";
import { statusMeta, formatDuration, formatMinutes, npsMeta, saleCell, shortDate } from "./reportRules";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const line = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const tone = (hex, key) => ({ backgroundColor: alpha(hex, 0.12), color: stateText(theme, hex, key), borderColor: alpha(hex, 0.3) });
  return {
    paper: { width: 640, maxWidth: "calc(100vw - 32px)", margin: 16 },
    header: { padding: "18px 22px 14px", gap: 12, alignItems: "center" },
    avatar: { width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#07080D", flex: "none" },
    title: { fontSize: 14, fontWeight: 700, color: theme.palette.text.primary, "& span": { color: theme.palette.aeros.textTertiary, fontWeight: 500 } },
    sub: { fontSize: 11.5, color: theme.palette.text.secondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    status: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, borderRadius: 99, padding: "3px 10px", whiteSpace: "nowrap", "& i": { width: 6, height: 6, borderRadius: "50%", background: "currentColor" } },
    info: tone(AEROS.cyan, "info"), success: tone(AEROS.success, "success"), warning: tone(AEROS.warning, "warning"), error: tone(AEROS.error, "error"), purple: tone("#C08BFF", "purple"),
    muted: { backgroundColor: isLight ? theme.palette.action.hover : "rgba(255,255,255,0.06)", color: theme.palette.aeros.textTertiary, borderColor: line },
    iconBtn: { width: 32, height: 32, borderRadius: 9, border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`, color: theme.palette.text.secondary, padding: 0, "& .MuiSvgIcon-root": { fontSize: 17 } },
    metas: { display: "grid", gridTemplateColumns: "repeat(4,1fr)", borderBottom: `1px solid ${line}`, backgroundColor: isLight ? theme.palette.action.hover : "rgba(255,255,255,0.02)" },
    meta: { padding: "10px 16px", borderRight: `1px solid ${line}`, "&:last-child": { borderRight: "none" } },
    metaLabel: { fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.palette.aeros.textTertiary },
    metaValue: { fontSize: 13, fontWeight: 600, marginTop: 3, color: theme.palette.text.primary, fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    tabs: { display: "flex", gap: 2, padding: "0 20px", borderBottom: `1px solid ${line}` },
    tab: { padding: "10px 14px", fontSize: 12.5, fontWeight: 500, color: theme.palette.text.secondary, background: "none", border: "none", borderBottom: "2px solid transparent", cursor: "pointer", font: "inherit", display: "flex", alignItems: "center", gap: 6 },
    tabOn: { fontWeight: 700, color: theme.palette.text.primary, borderImage: "linear-gradient(90deg,#A044FF,#22D3EE) 1" },
    count: { backgroundColor: isLight ? theme.palette.action.hover : "rgba(255,255,255,0.07)", borderRadius: 6, padding: "1px 7px", fontSize: 11 },
    body: { padding: "16px 20px", backgroundColor: theme.palette.aeros.surface, maxHeight: 380, overflowY: "auto", overflowX: "hidden", ...theme.scrollbarStyles },
    logRow: { display: "flex", gap: 12 },
    logCol: { display: "flex", flexDirection: "column", alignItems: "center", flex: "none", width: 26 },
    logDot: { width: 26, height: 26, borderRadius: "50%", border: "1px solid", display: "flex", alignItems: "center", justifyContent: "center", "& .MuiSvgIcon-root": { fontSize: 13 } },
    logLine: { flex: 1, width: 1, backgroundColor: isLight ? theme.palette.divider : "rgba(255,255,255,0.08)", minHeight: 14 },
    logMain: { flex: 1, minWidth: 0, paddingBottom: 12 },
    logTitle: { display: "flex", alignItems: "baseline", gap: 8, fontSize: 13, fontWeight: 600, color: theme.palette.text.primary, "& time": { marginLeft: "auto", fontSize: 10.5, color: theme.palette.aeros.textTertiary, fontWeight: 400, fontVariantNumeric: "tabular-nums", flex: "none" } },
    logDesc: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 2, lineHeight: 1.5 },
    day: { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.palette.aeros.textTertiary, padding: "4px 0 10px" },
    bubbleWrap: { display: "flex", flexDirection: "column", gap: 8 },
    bubble: { maxWidth: "78%", padding: "8px 12px", fontSize: 12.5, lineHeight: 1.5, color: theme.palette.text.primary, border: "1px solid", overflowWrap: "anywhere", whiteSpace: "pre-wrap", "& small": { display: "block", fontSize: 10.5, marginTop: 3, color: theme.palette.aeros.textTertiary } },
    bIn: { alignSelf: "flex-start", backgroundColor: theme.palette.background.paper, borderColor: line, borderRadius: "12px 12px 12px 4px" },
    bOut: { alignSelf: "flex-end", backgroundColor: alpha("#A044FF", isLight ? 0.08 : 0.16), borderColor: alpha("#A044FF", 0.3), borderRadius: "12px 12px 4px 12px" },
    sys: { alignSelf: "center", fontSize: 11, color: theme.palette.aeros.textTertiary, padding: "2px 10px", borderRadius: 99, backgroundColor: isLight ? theme.palette.action.hover : "rgba(255,255,255,0.05)" },
    footer: { padding: "12px 20px", borderTop: `1px solid ${line}`, display: "flex", alignItems: "center", gap: 10 },
    footInfo: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: theme.palette.text.secondary, "& b": { color: theme.palette.text.primary }, "& .MuiSvgIcon-root": { fontSize: 15 } },
    empty: { padding: "24px 0", textAlign: "center", fontSize: 12.5, color: theme.palette.text.secondary },
    okText: { color: `${stateText(theme, AEROS.success, "success")} !important` },
  };
});

const t = (k, o) => i18n.t(`aeros.reports.${k}`, o);

export default function TicketReportModal({ open, onClose, ticket }) {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const history = useHistory();
  const [tab, setTab] = useState("timeline");
  const [logs, setLogs] = useState(null);
  const [messages, setMessages] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    if (!ticket?.id) return undefined;
    let alive = true;
    setLogs(null); setMessages(null); setTab("timeline");
    api.get(`/tickets-log/${ticket.id}`).then(({ data }) => alive && setLogs(Array.isArray(data) ? data : [])).catch((e) => { toastError(e); if (alive) setLogs([]); });
    api.get(`/messages/${ticket.id}`, { params: { pageNumber: 1 } }).then(({ data }) => alive && setMessages(data.messages || [])).catch(() => alive && setMessages([]));
    return () => { alive = false; };
  }, [ticket?.id]);

  const timeline = useMemo(() => buildTimeline(logs || [], { showAccess: false }), [logs]);
  if (!ticket) return null;
  const st = statusMeta(ticket.status);
  const nps = npsMeta(ticket.NPS);
  const sale = saleCell(ticket);
  const color = avatarColorFor(ticket.contactName || "");
  const closed = ticket.closedAt ? shortDate(ticket.closedAt) : t("stillOpen");

  const exportPdf = () => {
    if (!printRef.current) return;
    html2pdf().from(printRef.current).set({ margin: 8, filename: `ticket-${ticket.id}.pdf`, image: { type: "jpeg", quality: 0.95 }, html2canvas: { scale: 2 }, jsPDF: { unit: "mm", format: "a4" } }).save();
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth={false} classes={{ paper: classes.paper }}>
      <div className={m.form} ref={printRef}>
        <div className={`${m.header} ${classes.header}`}>
          <div className={classes.avatar} style={{ backgroundColor: color }}>{initialsOf(ticket.contactName || "?")}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={classes.title}>{ticket.contactName || t("ticket.noContact")} <span>#{ticket.id}</span></div>
            <div className={classes.sub}>{[ticket.queueName, ticket.userName, ticket.whatsappName].filter(Boolean).join(" · ")} · {shortDate(ticket.createdAt)} → {closed}</div>
          </div>
          <span className={`${classes.status} ${classes[st.tone]}`}><i />{st.label}</span>
          <IconButton className={classes.iconBtn} onClick={exportPdf} title={t("ticket.exportPdf")} data-html2canvas-ignore><PictureAsPdfOutlinedIcon /></IconButton>
          <IconButton className={m.closeButton} onClick={onClose} aria-label={i18n.t("aeros.header.close")} data-html2canvas-ignore><CloseIcon /></IconButton>
        </div>
        <div className={classes.metas}>
          {[
            [t("ticket.wait"), ticket.waitMinutes != null ? formatMinutes(ticket.waitMinutes) : "—", ticket.waitMinutes != null && ticket.waitMinutes <= 5 ? classes.okText : ""],
            [t("ticket.duration"), formatDuration(ticket.supportTime)],
            [t("ticket.messages"), ticket.messagesCount ?? (messages ? messages.length : "—")],
            [t("ticket.reason"), ticket.motivo || "—"],
          ].map(([label, value, cls]) => (
            <div key={label} className={classes.meta}><div className={classes.metaLabel}>{label}</div><div className={`${classes.metaValue} ${cls || ""}`}>{value}</div></div>
          ))}
        </div>
        <div className={classes.tabs} data-html2canvas-ignore>
          <button type="button" className={`${classes.tab} ${tab === "timeline" ? classes.tabOn : ""}`} onClick={() => setTab("timeline")}>{t("ticket.timeline")}</button>
          <button type="button" className={`${classes.tab} ${tab === "chat" ? classes.tabOn : ""}`} onClick={() => setTab("chat")}>{t("ticket.chat")} {messages && <span className={classes.count}>{messages.length}</span>}</button>
        </div>
        <div className={classes.body}>
          {tab === "timeline" ? (
            logs === null ? <div className={classes.empty}><CircularProgress size={20} /></div>
            : !timeline.length ? <div className={classes.empty}>{t("ticket.noEvents")}</div>
            : timeline.map((g) => (
              <div key={g.day}>
                {timeline.length > 1 && <div className={classes.day}>{g.day}</div>}
                {g.items.map((l, i) => {
                  const d = describeLog(l);
                  const last = i === g.items.length - 1;
                  return (
                    <div key={l.id} className={classes.logRow}>
                      <div className={classes.logCol}>
                        <div className={`${classes.logDot} ${classes[toneOf(l.type)]}`}><i style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} /></div>
                        {!last && <div className={classes.logLine} />}
                      </div>
                      <div className={classes.logMain}>
                        <div className={classes.logTitle}>{d.text}<time>{timeLabel(l.createdAt)}{l.elapsed ? ` · ${l.elapsed}` : ""}</time></div>
                        {(d.actor || d.meta) && <div className={classes.logDesc}>{[d.actor, d.meta].filter(Boolean).join(" · ")}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          ) : (
            messages === null ? <div className={classes.empty}><CircularProgress size={20} /></div>
            : !messages.length ? <div className={classes.empty}>{t("ticket.noMessages")}</div>
            : <div className={classes.bubbleWrap}>
                {messages.map((msg) => (
                  <div key={msg.id} className={`${classes.bubble} ${msg.fromMe ? classes.bOut : classes.bIn}`}>
                    {msg.body || `[${msg.mediaType}]`}
                    <small>{new Date(msg.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}{msg.fromMe && ticket.userName ? ` · ${ticket.userName.split(" ")[0]}` : ""}</small>
                  </div>
                ))}
              </div>
          )}
        </div>
        <div className={classes.footer}>
          <div className={classes.footInfo}>
            <StarIcon style={{ color: AEROS.warning }} /> NPS <b>{nps ? nps.rate : "—"}</b>
            <span>·</span>
            <LocalOfferIcon style={{ color: AEROS.success }} /> {t("ticket.sale")} <b className={sale.kind === "sold" ? classes.okText : ""}>{sale.kind === "none" ? "—" : sale.text}</b>
          </div>
          <div style={{ marginLeft: "auto" }} data-html2canvas-ignore>
            <Button className={m.cancelButton} startIcon={<OpenInNewIcon />} onClick={() => history.push(`/tickets/${ticket.uuid}`)}>{t("ticket.openInTickets")}</Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
